import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LogOut,
  Search,
  FileText,
  Calendar,
  Plus,
  Clock,
  CheckCircle2,
  AlertCircle,
  Building2,
  Eye,
  TrendingUp,
  Download,
  RefreshCw,
  AlertTriangle,
  CreditCard,
  Filter,
  X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    completada: 'bg-green-100 text-green-700 border-green-200',
    pendiente: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };

  const icons = {
    completada: CheckCircle2,
    pendiente: AlertCircle,
  };

  const Icon = icons[status] || AlertCircle;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pendiente}`}>
      <Icon className="w-3.5 h-3.5" />
      {status === 'completada' ? 'Completada' : 'Pendiente'}
    </span>
  );
};

export default function ClinicaDashboard() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [filteredSolicitudes, setFilteredSolicitudes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [clinicaData, setClinicaData] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const navigate = useNavigate();

  // Filter states
  const [selectedEstado, setSelectedEstado] = useState('todos');
  const [selectedPeriodo, setSelectedPeriodo] = useState('30');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Periodo options
  const periodoOptions = [
    { value: '0', label: 'Hoy' },
    { value: '7', label: 'Últimos 7 días' },
    { value: '15', label: 'Últimos 15 días' },
    { value: '30', label: 'Últimos 30 días' },
    { value: '60', label: 'Últimos 60 días' },
    { value: '90', label: 'Últimos 90 días' },
    { value: 'todas', label: 'Todas' },
    { value: 'personalizado', label: 'Personalizado' },
  ];

  useEffect(() => {
    const token = localStorage.getItem('clinica_token');
    const data = localStorage.getItem('clinica_data');

    if (!token || !data) {
      navigate('/clinica/login');
      return;
    }

    setClinicaData(JSON.parse(data));
    fetchSolicitudes();
    fetchCreditStatus();
  }, [navigate]);

  // Apply filters whenever filter states change
  useEffect(() => {
    applyFilters();
  }, [solicitudes, searchTerm, selectedEstado, selectedPeriodo, fechaInicio, fechaFin]);

  const applyFilters = () => {
    let filtered = [...solicitudes];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.patient_info?.nombre?.toLowerCase().includes(term) ||
          s.id?.toLowerCase().includes(term) ||
          s.id_corto?.toLowerCase().includes(term)
      );
    }
    
    // Filter by estado
    if (selectedEstado && selectedEstado !== 'todos') {
      filtered = filtered.filter((s) => s.status === selectedEstado);
    }
    
    // Filter by periodo
    if (selectedPeriodo && selectedPeriodo !== 'todas') {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (selectedPeriodo === 'personalizado') {
        if (fechaInicio) {
          filtered = filtered.filter((s) => s.fecha >= fechaInicio);
        }
        if (fechaFin) {
          filtered = filtered.filter((s) => s.fecha <= fechaFin);
        }
      } else {
        const days = parseInt(selectedPeriodo);
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - days);
        startDate.setHours(0, 0, 0, 0);
        const startDateStr = startDate.toISOString().split('T')[0];
        
        filtered = filtered.filter((s) => s.fecha >= startDateStr);
      }
    }
    
    setFilteredSolicitudes(filtered);
  };

  const clearFilters = () => {
    setSelectedEstado('todos');
    setSelectedPeriodo('30');
    setFechaInicio('');
    setFechaFin('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedEstado !== 'todos' || selectedPeriodo !== '30' || searchTerm;

  const handleExportFiltered = async () => {
    try {
      const token = localStorage.getItem('clinica_token');
      
      // Build query params
      const params = new URLSearchParams();
      if (selectedEstado !== 'todos') params.append('status', selectedEstado);
      
      if (selectedPeriodo !== 'todas') {
        const today = new Date();
        if (selectedPeriodo === 'personalizado') {
          if (fechaInicio) params.append('fecha_inicio', fechaInicio);
          if (fechaFin) params.append('fecha_fin', fechaFin);
        } else {
          const days = parseInt(selectedPeriodo);
          const startDate = new Date(today);
          startDate.setDate(startDate.getDate() - days);
          params.append('fecha_inicio', startDate.toISOString().split('T')[0]);
        }
      }
      
      const response = await axios.get(`${API}/clinica/solicitudes/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `mis_solicitudes_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Solicitudes exportadas');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar solicitudes');
    }
  };

  const fetchCreditStatus = async () => {
    const token = localStorage.getItem('clinica_token');
    try {
      const response = await axios.get(`${API}/clinica/saldo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCreditStatus(response.data);
    } catch (error) {
      console.error('Error fetching credit status:', error);
    }
  };

  const fetchSolicitudes = async () => {
    const token = localStorage.getItem('clinica_token');

    try {
      const response = await axios.get(`${API}/clinica/solicitudes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSolicitudes(response.data);
      setFilteredSolicitudes(response.data);
    } catch (error) {
      console.error('Error fetching solicitudes:', error);
      if (error.response?.status === 401) {
        toast.error('Sesión expirada');
        handleLogout();
      } else {
        toast.error('Error al cargar solicitudes');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('clinica_token');
    localStorage.removeItem('clinica_data');
    navigate('/clinica/login');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/clinica/nueva-solicitud')}
                className="h-10 bg-[#505BB3] hover:bg-[#404A9C] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nueva Solicitud
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="h-10 border-slate-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Clinic Info Banner */}
      <div className="bg-[#505BB3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {clinicaData?.nombre || 'Panel de Clínica'}
              </h1>
              <p className="text-white/80 text-sm">{clinicaData?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Credit Limit Alert */}
        {creditStatus?.limite_credito_habilitado && (
          <div className={`mb-6 rounded-xl border p-4 ${
            creditStatus.excede_limite 
              ? 'bg-red-50 border-red-200' 
              : creditStatus.saldo_pendiente >= creditStatus.limite_credito * 0.8
                ? 'bg-amber-50 border-amber-200'
                : 'hidden'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                creditStatus.excede_limite ? 'bg-red-100' : 'bg-amber-100'
              }`}>
                {creditStatus.excede_limite 
                  ? <AlertTriangle className="w-5 h-5 text-red-600" />
                  : <CreditCard className="w-5 h-5 text-amber-600" />
                }
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${
                  creditStatus.excede_limite ? 'text-red-800' : 'text-amber-800'
                }`}>
                  {creditStatus.excede_limite 
                    ? 'Límite de Crédito Excedido' 
                    : 'Crédito Próximo al Límite'
                  }
                </h3>
                <p className={`text-sm mt-1 ${
                  creditStatus.excede_limite ? 'text-red-600' : 'text-amber-600'
                }`}>
                  {creditStatus.excede_limite 
                    ? 'No podrás crear nuevas solicitudes hasta regularizar tu saldo.' 
                    : 'Tu saldo está cerca del límite de crédito permitido.'
                  }
                </p>
                <div className="flex flex-wrap gap-4 mt-2 text-sm">
                  <span className="text-slate-600">
                    <strong>Saldo:</strong> ${creditStatus.saldo_pendiente?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-slate-600">
                    <strong>Límite:</strong> ${creditStatus.limite_credito?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                  <span className={creditStatus.excede_limite ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                    <strong>Disponible:</strong> ${creditStatus.credito_disponible?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-slate-600">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            {/* Estado Filter */}
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-slate-400" />
              <Select value={selectedEstado} onValueChange={setSelectedEstado}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-estado">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="completada">Completada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Periodo Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
                <SelectTrigger className="w-[160px] h-9" data-testid="filter-periodo">
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent>
                  {periodoOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Date Range */}
            {selectedPeriodo === 'personalizado' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className="h-9 w-[140px]"
                  placeholder="Desde"
                  data-testid="filter-fecha-inicio"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="h-9 w-[140px]"
                  placeholder="Hasta"
                  data-testid="filter-fecha-fin"
                />
              </div>
            )}
            
            <div className="flex items-center gap-2 ml-auto">
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-9 text-slate-600 hover:text-slate-900"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              )}
              
              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportFiltered}
                className="h-9 text-green-600 border-green-200 hover:bg-green-50"
                data-testid="export-filtered"
              >
                <Download className="w-4 h-4 mr-1" />
                Exportar
              </Button>
            </div>
          </div>
          
          {/* Active filters badge */}
          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Filtros activos:</span>
                {selectedEstado !== 'todos' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">
                    {selectedEstado === 'pendiente' ? 'Pendiente' : 'Completada'}
                  </span>
                )}
                {selectedPeriodo !== 'todas' && selectedPeriodo !== '30' && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                    <Calendar className="w-3 h-3" />
                    {periodoOptions.find(p => p.value === selectedPeriodo)?.label || selectedPeriodo}
                  </span>
                )}
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                    <Search className="w-3 h-3" />
                    "{searchTerm}"
                  </span>
                )}
                <span className="text-xs text-slate-400 ml-2">
                  ({filteredSolicitudes.length} resultado{filteredSolicitudes.length !== 1 ? 's' : ''})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Solicitudes Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="bg-[#505BB3] px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2 text-white">
                <FileText className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Historial de Solicitudes</h2>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por paciente o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-10 w-full sm:w-64 bg-white border-0 focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-12 text-center text-slate-500">
                <div className="animate-spin w-8 h-8 border-2 border-[#505BB3] border-t-transparent rounded-full mx-auto mb-4"></div>
                Cargando solicitudes...
              </div>
            ) : filteredSolicitudes.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-1">
                  {searchTerm ? 'No se encontraron resultados' : 'Sin solicitudes'}
                </h3>
                <p className="text-slate-500 mb-4">
                  {searchTerm
                    ? 'Intente con otros términos de búsqueda'
                    : 'Comience creando su primera solicitud de pruebas'}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => navigate('/clinica/nueva-solicitud')}
                    className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Solicitud
                  </Button>
                )}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Resultados
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredSolicitudes.map((solicitud) => (
                    <tr key={solicitud.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          {solicitud.id_corto || `SOL-${solicitud.id.slice(0, 6).toUpperCase()}`}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {solicitud.patient_info.nombre}
                          </div>
                          <div className="text-sm text-slate-500">
                            {solicitud.patient_info.especie} • {solicitud.patient_info.raza || 'Sin raza'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {solicitud.fecha}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Clock className="w-4 h-4 text-slate-400" />
                          {solicitud.hora_toma}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={solicitud.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {solicitud.resultado_pdf_id ? (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${API}/solicitudes/${solicitud.id}/resultado-pdf`, '_blank');
                              }}
                              className="h-8 border-green-300 text-green-700 hover:bg-green-50"
                            >
                              <Download className="w-3.5 h-3.5 mr-1" />
                              PDF
                            </Button>
                            {solicitud.resultado_pdf_version > 1 && (
                              <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                <RefreshCw className="w-3 h-3" />
                                v{solicitud.resultado_pdf_version}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Pendiente</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/clinica/solicitud/${solicitud.id}`)}
                          className="border-[#505BB3] text-[#505BB3] hover:bg-[#505BB3] hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-1.5" />
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Table Footer */}
          {filteredSolicitudes.length > 0 && (
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Mostrando <span className="font-medium">{filteredSolicitudes.length}</span> de{' '}
                <span className="font-medium">{solicitudes.length}</span> solicitudes
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
